// src/services/social-auth-service.js
// Social OAuth — initiate/disconnect connections for Facebook, Instagram, TikTok.
// All other platforms (YouTube, X, LinkedIn, Pinterest) are manual URL entry only.

import { db } from '../config/firebase-config';
import { doc, onSnapshot, updateDoc, deleteField } from 'firebase/firestore';

const FUNCTIONS_BASE = 'https://us-west1-divine-frameworks-6cb16.cloudfunctions.net';

// Platforms that support OAuth (vs manual URL-only)
export const OAUTH_PLATFORMS = ['facebook', 'instagram', 'tiktok'];

/**
 * Initiates OAuth flow for the given platform.
 * Fetches the redirect URL from Cloud Function and navigates the browser to it.
 * @param {string} platform — 'facebook' | 'instagram' | 'tiktok'
 * @param {string} uid — Firebase Auth user UID
 */
export async function initiateSocialAuth(platform, uid) {
  const fnName = `initiate${capitalize(platform)}Auth`;
  const res = await fetch(`${FUNCTIONS_BASE}/${fnName}?uid=${encodeURIComponent(uid)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to start ${platform} auth`);
  }
  const { url } = await res.json();
  window.location.href = url;
}

/**
 * Disconnects a platform — clears the socialConnections entry on the main doc.
 * Uses the client SDK (practitioners have write access to their own doc).
 * @param {string} platform
 * @param {string} uid
 */
export async function disconnectSocialPlatform(platform, uid) {
  const practRef = doc(db, 'practitioners', uid);
  await updateDoc(practRef, {
    [`socialConnections.${platform}`]: deleteField(),
  });
}

/**
 * Subscribes to social connection status from the main practitioner doc.
 * Returns an unsubscribe function.
 * @param {string} uid
 * @param {function} callback — called with socialConnections object
 */
export function subscribeSocialConnections(uid, callback) {
  const practRef = doc(db, 'practitioners', uid);
  return onSnapshot(
    practRef,
    (snap) => callback(snap.data()?.socialConnections || {}),
    (err) => {
      console.warn('subscribeSocialConnections error:', err);
      callback({});
    }
  );
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
